import React, { useEffect, useState } from 'react';
import type { Player, UpdatePlayerContactRequest } from '../../types/player';

interface PlayerContactFormProps {
  player: Player;
  saving: boolean;
  onSubmit: (data: UpdatePlayerContactRequest) => void;
}

const inputClass =
  'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const fromPlayer = (player: Player): Required<UpdatePlayerContactRequest> => ({
  phone: player.phone ?? '',
  address: player.address ?? '',
  emergencyContactName: player.emergencyContactName ?? '',
  emergencyContactPhone: player.emergencyContactPhone ?? '',
  emergencyContactRelation: player.emergencyContactRelation ?? '',
  medicalNotes: player.medicalNotes ?? '',
});

/**
 * Elérhetőségek és egészségügyi adatok szerkesztése. A szülő és a játékos maga
 * is kitöltheti - a többi adatot (név, TAJ, korosztály) a stáb kezeli.
 */
const PlayerContactForm: React.FC<PlayerContactFormProps> = ({ player, saving, onSubmit }) => {
  const [form, setForm] = useState(fromPlayer(player));

  useEffect(() => {
    setForm(fromPlayer(player));
  }, [player]);

  const set = (key: keyof UpdatePlayerContactRequest) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block text-sm font-medium text-gray-700">
          A játékos telefonszáma
          <input type="tel" maxLength={30} value={form.phone} onChange={set('phone')} className={inputClass} placeholder="ha van saját" />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Lakcím
          <input type="text" maxLength={200} value={form.address} onChange={set('address')} className={inputClass} />
        </label>
      </div>

      <fieldset className="rounded-lg border border-gray-200 p-3">
        <legend className="px-1 text-sm font-medium text-gray-700">Vészhelyzeti kontakt (ha a szülők nem érhetők el)</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block text-sm text-gray-700">
            Név
            <input type="text" maxLength={100} value={form.emergencyContactName} onChange={set('emergencyContactName')} className={inputClass} />
          </label>
          <label className="block text-sm text-gray-700">
            Telefon
            <input type="tel" maxLength={30} value={form.emergencyContactPhone} onChange={set('emergencyContactPhone')} className={inputClass} />
          </label>
          <label className="block text-sm text-gray-700">
            Kapcsolat
            <input
              type="text"
              maxLength={50}
              value={form.emergencyContactRelation}
              onChange={set('emergencyContactRelation')}
              className={inputClass}
              placeholder="pl. nagymama"
            />
          </label>
        </div>
      </fieldset>

      <label className="block text-sm font-medium text-gray-700">
        Egészségügyi tudnivalók
        <textarea
          rows={3}
          maxLength={1000}
          value={form.medicalNotes}
          onChange={set('medicalNotes')}
          className={inputClass}
          placeholder="Allergia, asztma, rendszeres gyógyszer - amit az edzőnek a pályán tudnia kell"
        />
        <span className="mt-1 block text-xs font-normal text-gray-500">
          Csak a klub stábja és te látod, a csapattársak és szüleik nem.
        </span>
      </label>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="w-full rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 sm:w-auto"
        >
          {saving ? 'Mentés...' : 'Mentés'}
        </button>
      </div>
    </form>
  );
};

export default PlayerContactForm;
